import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, openProposalPdf } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  FileSignature, Download, Lock, FileText, ExternalLink,
} from 'lucide-react';

const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700',
  'Pending approval': 'bg-amber-100 text-amber-700',
  Generated: 'bg-blue-100 text-blue-700',
  Sent: 'bg-indigo-100 text-indigo-700',
  Accepted: 'bg-emerald-100 text-emerald-700',
  Won: 'bg-emerald-100 text-emerald-700',
  Lost: 'bg-rose-100 text-rose-700',
  Cancelled: 'bg-slate-100 text-slate-500',
  Superseded: 'bg-slate-100 text-slate-500',
};

function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

/**
 * Reusable Proposals block for Client Profile, Lead Detail, etc.
 *
 * Props:
 *   clientId / leadId  – pass exactly one. Determines which endpoint is used.
 *   title              – optional header text
 *   dense              – render as compact strip (no header card)
 */
export default function ProposalsTab({ clientId, leadId, title = 'Proposals', dense = false }) {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState(null);
  const canSeeMoney = hasPerm('see_money');
  const canCreate = hasPerm('proposals_create');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = clientId ? `/clients/${clientId}/proposals` : `/leads/${leadId}/proposals`;
        const { data } = await api.get(url);
        if (!cancelled) setRows(data || []);
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          if (e?.response?.status !== 403) toast.error('Could not load proposals');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, leadId]);

  if (rows === null) {
    return (
      <Card className="shine-card p-5 rounded-xl space-y-2" data-testid="proposals-tab-loading">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-16 w-full" />
      </Card>
    );
  }

  return (
    <Card className={`shine-card ${dense ? 'p-3' : 'p-5'} rounded-xl`} data-testid="proposals-tab">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-green-ink" />
          {title}
          <Badge variant="outline" className="ml-1 text-[10px]">{rows.length}</Badge>
        </h2>
        {canCreate && (
          <Link to={`/proposals/new${clientId ? `?client_id=${clientId}` : leadId ? `?lead_id=${leadId}` : ''}`}
                className="text-xs text-green-ink hover:underline">
            + New proposal
          </Link>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="py-6 text-center text-ink-2 text-sm">
          <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
          No proposals yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ink-2 uppercase border-b border-ink/10">
                <th className="py-2 pr-2">Number</th>
                <th className="py-2 pr-2">Date</th>
                <th className="py-2 pr-2">Services</th>
                <th className="py-2 pr-2 text-right">Amount</th>
                <th className="py-2 pr-2">Prepared by</th>
                <th className="py-2 pr-2">Sent to</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-ink/5 hover:bg-page/50"
                    data-testid={`proposal-row-${r.id}`}>
                  <td className="py-2 pr-2 font-mono">
                    <Link to={`/proposals/${r.id}`} className="text-ink hover:underline">
                      {r.number}
                    </Link>
                    {r.revision > 1 && (
                      <Badge variant="outline" className="ml-1 text-[10px]">R{r.revision}</Badge>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-ink-2">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 pr-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.services || []).map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px]">
                          {s.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums">
                    {r.money_hidden
                      ? <span className="inline-flex items-center gap-1 text-ink-2" title="You can only see amounts for proposals you prepared.">
                          <Lock className="h-3 w-3" />—
                        </span>
                      : fmtINR(r.total)}
                  </td>
                  <td className="py-2 pr-2 text-ink-2">{r.prepared_by_name || r.created_by_name || '—'}</td>
                  <td className="py-2 pr-2 text-ink-2">
                    {(r.to_emails && r.to_emails.length > 0)
                      ? r.to_emails.slice(0, 2).join(', ') + (r.to_emails.length > 2 ? ` +${r.to_emails.length - 2}` : '')
                      : '—'}
                  </td>
                  <td className="py-2 pr-2">
                    <Badge className={`${STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-700'} border-0`}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <Button size="sm" variant="outline"
                            onClick={() => openProposalPdf(r.id, {
                              filename: `${r.number || 'proposal'}.pdf`,
                              onError: (m) => toast.error(m),
                            })}
                            data-testid={`proposal-tab-pdf-${r.id}`}>
                      <Download className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
